import { useCallback, useMemo, useRef, useState } from 'react';
import { createCollection } from '@exonet/jsonapi-data-collections';

export default ({ isOpen, activeTabKey, customerId, search, indexKeys }) => {
  const latestRequestIdRef = useRef(0);

  const [isSearching, setIsSearching] = useState(false);
  const [collection, setCollection] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const resetSearchState = useCallback(() => {
    latestRequestIdRef.current += 1; // invalidate any in-flight responses
    setIsSearching(false);
    setCollection(null);
    setHasSearched(false);
  }, []);

  const runSearch = useCallback(
    async (trimmedQuery) => {
      if (!isOpen || indexKeys.length === 0) return;

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      setIsSearching(true);
      setHasSearched(true);

      try {
        const result = await Promise.resolve(
          search(trimmedQuery, customerId, activeTabKey, indexKeys),
        );

        if (latestRequestIdRef.current !== requestId) return;

        const resources = result?.data ?? [];
        const meta = result?.meta ?? null;

        setCollection(createCollection(resources, meta));
      } finally {
        if (latestRequestIdRef.current === requestId) {
          setIsSearching(false);
        }
      }
    },
    [search, customerId, activeTabKey, isOpen, indexKeys],
  );

  return useMemo(
    () => ({
      collection,
      isSearching,
      runSearch,
      resetSearchState,
      hasSearched,
    }),
    [collection, isSearching, hasSearched, runSearch, resetSearchState],
  );
};
